import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/includes/DashboardLayout';
import { getMyProfile, updatePassword, User } from '../../api/users';
import { useToast } from '../../hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Shield, User as UserIcon, Lock, Mail, Phone, Calendar, Loader2 } from "lucide-react";
import { Separator } from "../../components/ui/separator";

const Profil: React.FC = () => {
    const { toast } = useToast();
    const [profile, setProfile] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

    const fetchProfile = React.useCallback(async () => {
        try {
            const data = await getMyProfile();
            setProfile(data);
        } catch (error) {
            console.error("Failed to fetch profile", error);
            toast({
                title: "Erreur",
                description: "Impossible de charger les informations du profil.",
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);



    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmitPassword = async (e: React.FormEvent) => {
        e.preventDefault();

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            toast({
                title: "Erreur",
                description: "Les nouveaux mots de passe ne correspondent pas.",
                variant: "destructive"
            });
            return;
        }

        if (passwordData.newPassword.length < 8) {
            toast({
                title: "Erreur",
                description: "Le nouveau mot de passe doit contenir au moins 8 caractères.",
                variant: "destructive"
            });
            return;
        }

        setIsUpdatingPassword(true);
        try {
            // We pass an empty string for ID because the API function uses the /me endpoint
            // but the function signature expects an ID. We should update the API function to not require it strictly
            // or just pass a dummy one if the function ignores it (which it does based on my impl).
            // Actually, let's pass profile.id just in case, though api/users.ts ignores it for updatePassword logic I wrote.
            await updatePassword(profile?.id || '', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword,
                confirmNewPassword: passwordData.confirmPassword
            });

            toast({
                title: "Succès",
                description: "Votre mot de passe a été mis à jour.",
            });
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error: any) {
            console.error("Failed to update password", error);
            toast({
                title: "Erreur",
                description: error.response?.data?.message || "Impossible de mettre à jour le mot de passe.",
                variant: "destructive"
            });
        } finally {
            setIsUpdatingPassword(false);
        }
    };

    const getInitials = (firstName: string, lastName: string) => {
        return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex justify-center items-center h-full">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
            </DashboardLayout>
        );
    }

    if (!profile) {
        return (
            <DashboardLayout>
                <div className="flex flex-col justify-center items-center h-[60vh] text-center">
                    <UserIcon className="h-16 w-16 text-gray-300 mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Profil non disponible</h2>
                    <p className="text-muted-foreground mb-6 max-w-md">
                        Impossible de charger les informations du profil. Veuillez vérifier votre connexion ou réessayer plus tard.
                    </p>
                    <Button onClick={fetchProfile} variant="outline">
                        Réessayer
                    </Button>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6 max-w-5xl mx-auto pb-10">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Mon Profil</h1>
                    <p className="text-muted-foreground mt-1">
                        Gérez vos informations personnelles et votre sécurité.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* User Info Card */}
                    <Card className="md:col-span-1 shadow-sm border-t-4 border-t-blue-600">
                        <CardHeader className="text-center pb-2">
                            <div className="mx-auto mb-4 relative">
                                <Avatar className="h-32 w-32 border-4 border-white dark:border-gray-800 shadow-lg">
                                    <AvatarImage src={profile.avatar} />
                                    <AvatarFallback className="text-4xl bg-blue-100 text-blue-700 font-bold">
                                        {getInitials(profile.firstName, profile.lastName)}
                                    </AvatarFallback>
                                </Avatar>
                                {profile.isVerified && (
                                    <div className="absolute bottom-2 right-2 bg-blue-600 text-white p-1.5 rounded-full shadow-md border-2 border-white dark:border-gray-800" title="Compte vérifié">
                                        <Shield className="h-4 w-4" />
                                    </div>
                                )}
                            </div>
                            <CardTitle className="text-2xl">{profile.firstName} {profile.lastName}</CardTitle>
                            <CardDescription className="flex items-center justify-center gap-2 mt-1">
                                <Badge variant="secondary" className="px-3 py-1 text-sm font-medium">
                                    {profile.role}
                                </Badge>
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4 pt-6">
                            <div className="space-y-3">
                                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                                    <Mail className="h-4 w-4 text-gray-400" />
                                    <span className="truncate">{profile.email}</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                                    <Phone className="h-4 w-4 text-gray-400" />
                                    <span>{profile.phone || 'Non renseigné'}</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                                    <Calendar className="h-4 w-4 text-gray-400" />
                                    <span>Membre depuis le {formatDate(profile.createdAt)}</span>
                                </div>
                            </div>

                            <Separator />

                            <div className="pt-2">
                                <div className="text-xs font-semibold uppercase text-gray-400 mb-2">Statut du compte</div>
                                <div className="flex items-center gap-2">
                                    <div className={`h-2.5 w-2.5 rounded-full ${profile.isActive ? 'bg-green-500' : 'bg-red-500'}`} />
                                    <span className="text-sm font-medium">{profile.isActive ? 'Actif' : 'Inactif'}</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Change Password Form */}
                    <Card className="md:col-span-2 shadow-sm">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Lock className="h-5 w-5 text-gray-500" />
                                Sécurité
                            </CardTitle>
                            <CardDescription>
                                Mettez à jour votre mot de passe pour sécuriser votre compte.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmitPassword} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="currentPassword">Mot de passe actuel</Label>
                                    <Input
                                        id="currentPassword"
                                        name="currentPassword"
                                        type="password"
                                        value={passwordData.currentPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Entre votre mot de passe actuel"
                                        required
                                    />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                                        <Input
                                            id="newPassword"
                                            name="newPassword"
                                            type="password"
                                            value={passwordData.newPassword}
                                            onChange={handlePasswordChange}
                                            placeholder="8 caractères minimum"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="confirmPassword">Confirmer le nouveau mot de passe</Label>
                                        <Input
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            type="password"
                                            value={passwordData.confirmPassword}
                                            onChange={handlePasswordChange}
                                            placeholder="Confirmer votre mot de passe"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-100 dark:border-yellow-900/50 text-sm text-yellow-800 dark:text-yellow-200">
                                    <h4 className="font-semibold mb-1 flex items-center gap-2">
                                        <Shield className="h-4 w-4" /> Recommandations de sécurité
                                    </h4>
                                    <ul className="list-disc list-inside space-y-1 ml-1 opacity-90">
                                        <li>Utilisez au moins 8 caractères.</li>
                                        <li>Incluez des majuscules, des minuscules et des chiffres.</li>
                                        <li>Ne partagez jamais votre mot de passe.</li>
                                    </ul>
                                </div>

                                <div className="flex justify-end pt-4">
                                    <Button type="submit" disabled={isUpdatingPassword} className="w-full md:w-auto">
                                        {isUpdatingPassword ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Mise à jour...
                                            </>
                                        ) : (
                                            'Mettre à jour le mot de passe'
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default Profil;
